<template>
  <div
    :id="MODALS_CONTAINER_ID"
    ref="container"
    class="modals-container"
    data-testid="modals-container"
  >
    <TransitionGroup name="modal">
      <div
        v-if="backdropEnabled"
        :key="-1"
        class="modals-backdrop"
        data-testid="modals-backdrop"
        :style="{ zIndex: backdropZIndex }"
      />
      <Component
        :is="modalOptions.component"
        v-for="(modalOptions, index) in modals"
        :key="index"
        v-bind="modalOptions.props"
        :style="{ zIndex: modalZIndex(index) }"
        v-on="modalOptions.events || {}"
      />
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, computed } from "vue";
import { ref } from "vue";

import type { ModalContainerOptions } from "shared/modals/ModalService";

import { MODALS_CONTAINER_ID } from "./constants";
import { provideModalOverlayTarget } from "./useModalOverlayTarget";

const container = ref<HTMLDivElement>();

// Anything anchored inside a modal has to paint above it; hand down the element
// rather than making descendants find it.
provideModalOverlayTarget(container);

const modals = ref<ModalContainerOptions<ComponentPublicInstance>[]>([]);

const backdropEnabled = computed<boolean>(
  () =>
    modals.value.length > 0 &&
    !modals.value.every((modalOptions) => modalOptions.noBackdrop)
);

const backdropZIndex = computed<number>(() => {
  const indexOfTopMostModalWithBackdrop = modals.value.findLastIndex(
    (modalContainerOptions) => !modalContainerOptions.noBackdrop
  );

  return modalZIndex(indexOfTopMostModalWithBackdrop);
});

function modalZIndex(modalArayIndex: number) {
  return 3000 + modalArayIndex;
}

function openModal(
  modalOptions: ModalContainerOptions<ComponentPublicInstance>
) {
  modals.value.push(modalOptions);
}

function closeModal(modalName: string) {
  const index = modals.value.findIndex(({ name }) => name === modalName);

  if (index > -1) {
    modals.value.splice(index, 1);
  }
}

function closeAllModals() {
  modals.value = [];
}

defineExpose({
  modals,
  openModal,
  closeModal,
  closeAllModals,
});
</script>

<style lang="scss" scoped>
.modals-container {
  position: fixed;
  z-index: 8000;
}

.modals-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0 0 0 / 50%);
  transition: opacity 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from :deep(.streem-modal),
.modal-leave-to :deep(.streem-modal) {
  transform: scale(1.1);
}
</style>
